import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import Image from "@tiptap/extension-image";
import { Extension } from "@tiptap/core";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Table as TableIcon, ImagePlus, Film, BarChart3, Rows3, Columns3, Trash2, Heading2, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ChartNode, VideoNode } from "@/components/editor/nodes";

const SIZES = [["", "Normal"], ["12px", "Small"], ["18px", "Large"], ["24px", "X-Large"], ["32px", "Huge"]];

// Block inserts (tables, charts, media) should land after any table the cursor is inside, never nested in a cell.
function insertBlock(editor, content) {
  const chain = editor.chain().focus();
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    if ($from.node(d).type.name === "table") {
      const after = $from.after(d);
      chain.insertContentAt(after, [...(Array.isArray(content) ? content : [content]), { type: "paragraph" }]).run();
      return;
    }
  }
  chain.insertContent([...(Array.isArray(content) ? content : [content]), { type: "paragraph" }]).run();
}

const SingleLine = Extension.create({
  name: "singleLine",
  addKeyboardShortcuts() { return { Enter: () => true, "Shift-Enter": () => true, "Mod-Enter": () => true }; },
});

export const isRichEmpty = (html) => !html || !html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();
export const richToText = (html) => (html || "").replace(/<\/(p|li|h[1-6]|div)>|<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ").trim();

function TBtn({ on, active, label, children, testid, disabled }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} aria-label={label} title={label} disabled={disabled} data-testid={testid}
      className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors disabled:opacity-40 ${active ? "bg-[#A8785A] text-[#FFFDF7]" : "text-[#8C7562] hover:bg-[#FFF3F8] hover:text-[#3A2E25]"}`}>
      {children}
    </button>
  );
}

function Toolbar({ editor, mode, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"),
      bullet: e.isActive("bulletList"), ordered: e.isActive("orderedList"), heading: e.isActive("heading", { level: 2 }),
      size: e.getAttributes("textStyle").fontSize || "", inTable: e.isActive("table"),
      canUndo: e.can().undo(), canRedo: e.can().redo(),
    }),
  });
  const doc = mode === "doc";
  const multi = mode !== "single";

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/organizer/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind === "video") insertBlock(editor, { type: "video", attrs: { src: data.url } });
      else insertBlock(editor, { type: "image", attrs: { src: data.url, alt: file.name } });
      toast.success(`${data.kind === "video" ? "Video" : "Image"} added to your notes.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 rounded-lg border border-[#E8DCCB] bg-[#FFFDF7] mb-1.5" data-testid={`${testid}-toolbar`}>
      <TBtn on={() => editor.chain().focus().undo().run()} disabled={!s.canUndo} label="Undo" testid={`${testid}-undo`}><Undo2 size={13} /></TBtn>
      <TBtn on={() => editor.chain().focus().redo().run()} disabled={!s.canRedo} label="Redo" testid={`${testid}-redo`}><Redo2 size={13} /></TBtn>
      <span className="w-px h-4 bg-[#E8DCCB] mx-1" />
      <TBtn on={() => editor.chain().focus().toggleBold().run()} active={s.bold} label="Bold" testid={`${testid}-bold`}><Bold size={13} /></TBtn>
      <TBtn on={() => editor.chain().focus().toggleItalic().run()} active={s.italic} label="Italic" testid={`${testid}-italic`}><Italic size={13} /></TBtn>
      <TBtn on={() => editor.chain().focus().toggleUnderline().run()} active={s.underline} label="Underline" testid={`${testid}-underline`}><UnderlineIcon size={13} /></TBtn>
      <select value={s.size} onMouseDown={(e) => e.stopPropagation()} data-testid={`${testid}-size`} aria-label="Font size"
        onChange={(e) => (e.target.value ? editor.chain().focus().setFontSize(e.target.value).run() : editor.chain().focus().unsetFontSize().run())}
        className="h-7 rounded-md border border-[#E8DCCB] bg-[#FFFDF7] text-[11px] px-1.5 text-[#57473B] outline-none ml-1">
        {SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {multi && (
        <>
          <span className="w-px h-4 bg-[#E8DCCB] mx-1" />
          <TBtn on={() => editor.chain().focus().toggleBulletList().run()} active={s.bullet} label="Bullet list" testid={`${testid}-bullets`}><List size={13} /></TBtn>
          <TBtn on={() => editor.chain().focus().toggleOrderedList().run()} active={s.ordered} label="Numbered list" testid={`${testid}-numbers`}><ListOrdered size={13} /></TBtn>
        </>
      )}
      {doc && (
        <>
          <TBtn on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={s.heading} label="Heading" testid={`${testid}-heading`}><Heading2 size={13} /></TBtn>
          <span className="w-px h-4 bg-[#E8DCCB] mx-1" />
          <TBtn on={() => { if (s.inTable) insertBlock(editor, { type: "paragraph" }); editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); }} label="Insert table" testid={`${testid}-table`}><TableIcon size={13} /></TBtn>
          {s.inTable && (
            <>
              <TBtn on={() => editor.chain().focus().addRowAfter().run()} label="Add row" testid={`${testid}-table-row`}><Rows3 size={13} /></TBtn>
              <TBtn on={() => editor.chain().focus().addColumnAfter().run()} label="Add column" testid={`${testid}-table-col`}><Columns3 size={13} /></TBtn>
              <TBtn on={() => editor.chain().focus().deleteTable().run()} label="Delete table" testid={`${testid}-table-delete`}><Trash2 size={13} /></TBtn>
            </>
          )}
          <span className="w-px h-4 bg-[#E8DCCB] mx-1" />
          <TBtn on={() => fileRef.current?.click()} label="Upload image or video" disabled={uploading} testid={`${testid}-media`}>{uploading ? <span className="w-3 h-3 rounded-full border-2 border-[#A8785A] border-t-transparent animate-spin" /> : <ImagePlus size={13} />}</TBtn>
          <TBtn on={() => fileRef.current?.click()} label="Upload video" disabled={uploading} testid={`${testid}-video`}><Film size={13} /></TBtn>
          <TBtn on={() => insertBlock(editor, { type: "chart", attrs: { chart: { title: "My chart", type: "bar", data: [{ label: "Jan", value: 10 }, { label: "Feb", value: 18 }, { label: "Mar", value: 14 }] } } })} label="Insert chart" testid={`${testid}-chart`}><BarChart3 size={13} /></TBtn>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-media-input`} />
        </>
      )}
    </div>
  );
}

export default function RichField({ value, onChange, placeholder, mode = "single", testid }) {
  const [focused, setFocused] = useState(false);
  const last = useRef(value);
  const doc = mode === "doc";
  const single = mode === "single";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: doc ? { levels: [2, 3] } : false,
        bulletList: single ? false : undefined, orderedList: single ? false : undefined, listItem: single ? false : undefined,
        blockquote: false, codeBlock: false, horizontalRule: false, code: false,
        hardBreak: single ? false : undefined, link: doc ? undefined : false,
      }),
      TextStyle, FontSize,
      Placeholder.configure({ placeholder: placeholder || "" }),
      ...(single ? [SingleLine] : []),
      ...(doc ? [TableKit.configure({ table: { resizable: false } }), Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "notes-image" } }), VideoNode, ChartNode] : []),
    ],
    content: value || "",
    editorProps: { attributes: { class: `rich-content ${mode}`, "data-testid": testid } },
    onUpdate: ({ editor: e }) => {
      const html = e.isEmpty ? "" : e.getHTML();
      last.current = html;
      onChange(html);
    },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });

  useEffect(() => {
    if (!editor || value === last.current) return;
    last.current = value;
    editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return <div className="field min-h-[46px]" />;
  const showBar = !single || focused;
  return (
    <div className={`rich rich-${mode}`} data-testid={`${testid}-wrap`}>
      {showBar && <Toolbar editor={editor} mode={mode} testid={testid} />}
      <EditorContent editor={editor} className={`field rich-field ${focused ? "is-focused" : ""}`} />
    </div>
  );
}
